import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import type { VirtualItem } from '@tanstack/react-virtual'
import { PostSkeleton } from '@/components/Skeleton'
import { PostCard } from '@/components/PostCard'
import { BatchVerificationProvider } from '@/contexts/BatchVerificationContext'
import { computeThreadPositions } from '@/lib/threadGrouping'
import { verificationIdentifierForAuthor } from '@/lib/apBridgedIdentity'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { strings } from '@/strings'
import type { PostListProps } from './PostList.types'

export function PostList(props: PostListProps) {
    const {
        flags: { virtualizedFeed: virtualizedFeedEnabled },
    } = useFeatureFlags()

    if (virtualizedFeedEnabled) {
        return <VirtualizedPostList {...props} />
    }
    return <SimplePostList {...props} />
}

// ---------------------------------------------------------------------------
// Non-virtualized path (default) — simple .map() with IntersectionObserver
// ---------------------------------------------------------------------------

function SimplePostList({
    posts,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    onLoadMore,
    isOwnProfile,
    currentUserDid,
    currentMastodonHandle,
    onLike,
    onRepost,
    onQuote,
    onDelete,
    onReply,
    onBookmark,
    onTogglePin,
    onAuthorClick,
    onPostClick,
    onRetry,
    error,
    // isVisible is intentionally unused in the simple path — the keep-alive visibility
    // logic (disabling the virtualizer, skipping scroll-margin updates) is only relevant
    // to the virtualized path. CSS hide/show does not affect .map() rendering.
}: PostListProps) {
    const sentinelRef = useRef<HTMLDivElement>(null)

    const threadPositions = useMemo(() => computeThreadPositions(posts), [posts])

    const allPostDids = useMemo(
        () => [
            ...new Set(
                posts
                    .map(post => verificationIdentifierForAuthor(post?.author))
                    .filter((d): d is string => !!d)
            ),
        ],
        [posts]
    )

    const hasNextPageRef = useRef(hasNextPage)
    const isFetchingNextPageRef = useRef(isFetchingNextPage)
    const onLoadMoreRef = useRef(onLoadMore)
    useEffect(() => {
        hasNextPageRef.current = hasNextPage
        isFetchingNextPageRef.current = isFetchingNextPage
        onLoadMoreRef.current = onLoadMore
    })

    // Infinite scroll: fire onLoadMore when the sentinel scrolls within 200px of the viewport.
    // Guard: don't observe until at least one page has loaded (posts.length > 0) to
    // prevent cascade fetches when the component mounts with an empty list.
    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel || posts.length === 0) return

        const observer = new IntersectionObserver(
            entries => {
                if (
                    entries[0].isIntersecting &&
                    hasNextPageRef.current &&
                    !isFetchingNextPageRef.current
                ) {
                    onLoadMoreRef.current?.()
                }
            },
            { rootMargin: '200px' }
        )

        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [posts.length])

    if (isLoading) {
        return (
            <div className="mt-4">
                {[...Array(3)].map((_, i) => (
                    <PostSkeleton key={i} showActions />
                ))}
            </div>
        )
    }

    if (error) {
        return (
            <div className="mt-4 px-4 py-8 text-center">
                <p className="text-text-secondary text-sm">Failed to load posts</p>
                <button
                    onClick={onRetry ?? onLoadMore}
                    className="text-primary text-sm mt-2 hover:underline"
                >
                    Retry
                </button>
            </div>
        )
    }

    if (posts.length === 0) {
        return (
            <div className="mt-4 px-4 py-8 text-center">
                <p className="text-text-secondary text-sm">No posts yet</p>
            </div>
        )
    }

    return (
        <BatchVerificationProvider dids={allPostDids}>
            <div className="mt-4">
                {posts.map(post => (
                    <PostRow
                        key={post.pinnedEntry ? `pinned-${post.uri}` : post.uri}
                        post={post}
                        isOwnProfile={isOwnProfile}
                        currentUserDid={currentUserDid}
                        currentMastodonHandle={currentMastodonHandle}
                        threadPositions={threadPositions}
                        onLike={onLike}
                        onRepost={onRepost}
                        onQuote={onQuote}
                        onDelete={onDelete}
                        onReply={onReply}
                        onBookmark={onBookmark}
                        onTogglePin={onTogglePin}
                        onAuthorClick={onAuthorClick}
                        onPostClick={onPostClick}
                    />
                ))}
                <FooterRow
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    postsLength={posts.length}
                />
                {/* Zero-height sentinel — IntersectionObserver fires when this enters
                    the viewport, triggering infinite scroll. Placed after FooterRow so
                    the trigger point is stable regardless of what FooterRow renders. */}
                <div ref={sentinelRef} style={{ height: 0 }} />
            </div>
        </BatchVerificationProvider>
    )
}

// ---------------------------------------------------------------------------
// Virtualized path — @tanstack/react-virtual window virtualizer
// ---------------------------------------------------------------------------

function VirtualizedPostList({
    posts,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    onLoadMore,
    isOwnProfile,
    currentUserDid,
    currentMastodonHandle,
    onLike,
    onRepost,
    onQuote,
    onDelete,
    onReply,
    onBookmark,
    onTogglePin,
    onAuthorClick,
    onPostClick,
    onRetry,
    error,
    isVisible,
}: PostListProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const [scrollMargin, setScrollMargin] = useState(0)

    // Persists item measurements (sizes) across enable/disable cycles caused by the
    // CSS keep-alive hide/show (isVisible=false/true). When enabled=false, TanStack
    // Virtual clears itemSizeCache inside getMeasurements(). Without this, re-enabling
    // falls back to estimateSize (400px) for all items. If actual sizes differ, resizeItem
    // fires _scrollToOffset adjustments for every item above the scroll position, pushing
    // window.scrollY toward 0 and making upward scroll impossible.
    // By saving measurementsCache here and passing it as initialMeasurementsCache, the
    // virtualizer re-populates itemSizeCache on re-enable from real sizes → delta ≈ 0
    // → no scroll adjustments.
    const savedMeasurementsCacheRef = useRef<VirtualItem[]>([])

    const threadPositions = useMemo(() => computeThreadPositions(posts), [posts])

    // Total rows = posts + 1 footer row (spinner / end-of-feed / load trigger)
    const totalItems = posts.length + 1

    const rowVirtualizer = useWindowVirtualizer({
        count: totalItems,
        estimateSize: index => (index < posts.length ? 400 : 40),
        overscan: 5,
        scrollMargin,
        enabled: isVisible !== false,
        // Reading .current during render is intentional: initialMeasurementsCache must be
        // provided synchronously so setOptions() (called before any getMeasurements() run)
        // has the saved sizes before the virtualizer re-enables. This pre-populates
        // itemSizeCache on re-enable, ensuring resizeItem finds delta≈0 and emits no
        // scroll adjustments. In Concurrent Mode, React may render this component multiple
        // times before committing; each render reads the same .current (set by the last
        // committed useLayoutEffect), so all speculative renders see the correct snapshot.
        // eslint-disable-next-line react-hooks/refs
        initialMeasurementsCache: savedMeasurementsCacheRef.current,
    })

    // Intentional: no dep array — the snapshot must stay current after every render
    // cycle that may update measurements (scroll, resize, image load). .slice() creates
    // a true snapshot rather than a reference to TanStack Virtual's internal array (safe
    // today since the library replaces the array on each getMeasurements run, but
    // defensive against future library changes).
    // NOTE: this effect depends on getVirtualItems() having already been called (line 96
    // below) to trigger getMeasurements() and populate measurementsCache from
    // initialMeasurementsCache on the re-enable render. Do NOT move getVirtualItems()
    // after the early returns — that would break the snapshot by reading an empty cache.
    useLayoutEffect(() => {
        if (isVisible !== false && rowVirtualizer.measurementsCache.length > 0) {
            savedMeasurementsCacheRef.current = rowVirtualizer.measurementsCache.slice()
        }
    })

    // Measure the container's document Y offset and keep it updated via ResizeObserver.
    // Skip while hidden (isVisible===false) — getBoundingClientRect() on display:none
    // returns stale/zero values. The effect re-runs when isVisible becomes true, so
    // the margin is refreshed before the virtualizer reads it.
    useLayoutEffect(() => {
        if (isVisible === false) return
        const updateMargin = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect()
                setScrollMargin(rect.top + window.scrollY)
            }
        }
        updateMargin()
        const ro = new ResizeObserver(updateMargin)
        ro.observe(document.body)
        return () => ro.disconnect()
    }, [isVisible])

    // NOTE: getVirtualItems() must be called here (before early returns) so that
    // getMeasurements() runs and populates measurementsCache from initialMeasurementsCache
    // on the re-enable render. The useLayoutEffect above snapshots measurementsCache
    // after this call. Moving this assignment after early returns would break that snapshot.
    const virtualItems = rowVirtualizer.getVirtualItems()

    // Collect DIDs for all loaded posts so all verifications are pre-fetched in a
    // single batch when the feed loads. Previously this was scoped to visible virtual
    // items, which caused a burst of API calls as each new item scrolled into view —
    // each scroll event added a new DID to visibleDids and fired an extra request.
    // By using all posts, verifications are fetched upfront and re-mounting a
    // virtualized item never triggers a new API call.
    const allPostDids = useMemo(
        () => [
            ...new Set(
                posts
                    .map(post => verificationIdentifierForAuthor(post?.author))
                    .filter((d): d is string => !!d)
            ),
        ],
        [posts]
    )

    // Refs so the load-more check always sees the latest values
    const hasNextPageRef = useRef(hasNextPage)
    const isFetchingNextPageRef = useRef(isFetchingNextPage)
    const onLoadMoreRef = useRef(onLoadMore)
    useEffect(() => {
        hasNextPageRef.current = hasNextPage
        isFetchingNextPageRef.current = isFetchingNextPage
        onLoadMoreRef.current = onLoadMore
    })

    // Infinite scroll: trigger onLoadMore when the footer row is within 200px of viewport bottom
    useEffect(() => {
        // Guard: don't trigger load-more before the first page has rendered.
        // Without this, an empty list remount immediately fires onLoadMore because
        // the footer item is visible at the top of an empty viewport.
        if (posts.length === 0) return
        const footerItem = virtualItems.find(vi => vi.index === posts.length)
        if (!footerItem) return
        if (!hasNextPageRef.current || isFetchingNextPageRef.current) return

        const footerStart = footerItem.start - scrollMargin
        const viewportBottom = window.scrollY + window.innerHeight
        if (footerStart - viewportBottom < 200) {
            onLoadMoreRef.current?.()
        }
    })

    // Handles true→true: Home button tapped while already on Home. The feed never
    // unmounts, so measure() is called to invalidate cached row heights, clearing
    // any overlap before the browser composites the new scroll position.
    useEffect(() => {
        if (!isVisible) return
        const handleScrollToTop = () => rowVirtualizer.measure()
        window.addEventListener('mackinac:scroll-to-top', handleScrollToTop)
        return () => window.removeEventListener('mackinac:scroll-to-top', handleScrollToTop)
    }, [isVisible, rowVirtualizer])

    // Capture-phase load events on the container to re-measure after images/iframes load
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleLoad = (e: Event) => {
            const target = e.target as HTMLElement
            if (target.tagName === 'IMG' || target.tagName === 'IFRAME') {
                const wrapper = target.closest<HTMLElement>('[data-index]')
                if (wrapper) {
                    rowVirtualizer.measureElement(wrapper)
                }
            }
        }

        container.addEventListener('load', handleLoad, true)
        return () => container.removeEventListener('load', handleLoad, true)
    }, [rowVirtualizer])

    if (isLoading) {
        return (
            <div className="mt-4">
                {[...Array(3)].map((_, i) => (
                    <PostSkeleton key={i} showActions />
                ))}
            </div>
        )
    }

    if (error) {
        return (
            <div className="mt-4 px-4 py-8 text-center">
                <p className="text-text-secondary text-sm">Failed to load posts</p>
                <button
                    onClick={onRetry ?? onLoadMore}
                    className="text-primary text-sm mt-2 hover:underline"
                >
                    Retry
                </button>
            </div>
        )
    }

    if (posts.length === 0) {
        return (
            <div className="mt-4 px-4 py-8 text-center">
                <p className="text-text-secondary text-sm">No posts yet</p>
            </div>
        )
    }

    return (
        <BatchVerificationProvider dids={allPostDids}>
            <div
                ref={containerRef}
                className="mt-4"
                style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}
            >
                {virtualItems.map(virtualItem => {
                    const isFooter = virtualItem.index === posts.length

                    return (
                        <div
                            key={virtualItem.key}
                            data-index={virtualItem.index}
                            ref={rowVirtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualItem.start - scrollMargin}px)`,
                            }}
                        >
                            {isFooter ? (
                                <FooterRow
                                    isFetchingNextPage={isFetchingNextPage}
                                    hasNextPage={hasNextPage}
                                    postsLength={posts.length}
                                />
                            ) : (
                                <PostRow
                                    post={posts[virtualItem.index]}
                                    isOwnProfile={isOwnProfile}
                                    currentUserDid={currentUserDid}
                                    currentMastodonHandle={currentMastodonHandle}
                                    threadPositions={threadPositions}
                                    onLike={onLike}
                                    onRepost={onRepost}
                                    onQuote={onQuote}
                                    onDelete={onDelete}
                                    onReply={onReply}
                                    onBookmark={onBookmark}
                                    onTogglePin={onTogglePin}
                                    onAuthorClick={onAuthorClick}
                                    onPostClick={onPostClick}
                                />
                            )}
                        </div>
                    )
                })}
            </div>
        </BatchVerificationProvider>
    )
}

import type { Post } from '@/types/posts'
import type { ThreadPosition } from '@/lib/threadGrouping'

interface PostRowProps {
    post: Post
    isOwnProfile: boolean
    currentUserDid?: string
    currentMastodonHandle?: string
    threadPositions: Map<string, ThreadPosition>
    onLike?: (post: Post) => void
    onRepost?: (post: Post) => void
    onQuote?: (post: Post) => void
    onDelete?: (post: Post) => void
    onReply?: (post: Post) => void
    onBookmark?: (post: Post) => void
    onTogglePin?: (post: Post) => void
    onAuthorClick?: (identifier: string) => void
    onPostClick?: (post: Post) => void
}

function PostRow({
    post,
    isOwnProfile,
    currentUserDid,
    currentMastodonHandle,
    threadPositions,
    onLike,
    onRepost,
    onQuote,
    onDelete,
    onReply,
    onBookmark,
    onTogglePin,
    onAuthorClick,
    onPostClick,
}: PostRowProps) {
    const isOwnPost =
        isOwnProfile ||
        post.author?.did === currentUserDid ||
        (!!currentMastodonHandle &&
            post.isActivityPub &&
            post.author?.handle?.toLowerCase() === currentMastodonHandle)

    // A pinned entry sits in its own slot — it must not inherit thread connector
    // lines from the chronological copy of the same URI.
    const tp = post.pinnedEntry ? undefined : threadPositions.get(post.uri)
    const showBorder = !tp || tp.position === 'last'

    return (
        <div className={showBorder ? 'border-b border-border-light dark:border-border-dark' : ''}>
            <PostCard
                post={post}
                isOwnPost={isOwnPost}
                threadPosition={tp}
                onLike={onLike}
                onRepost={onRepost}
                onQuote={onQuote}
                onDelete={isOwnPost ? onDelete : undefined}
                onReply={onReply}
                onBookmark={onBookmark}
                onTogglePin={isOwnPost ? onTogglePin : undefined}
                onAuthorClick={onAuthorClick}
                onPostClick={onPostClick}
            />
        </div>
    )
}

interface FooterRowProps {
    isFetchingNextPage: boolean
    hasNextPage: boolean
    postsLength: number
}

function FooterRow({ isFetchingNextPage, hasNextPage, postsLength }: FooterRowProps) {
    if (isFetchingNextPage) {
        return (
            <div role="status" aria-live="polite">
                <span className="sr-only">{strings.common.loadingMore}</span>
                <PostSkeleton showActions />
            </div>
        )
    }
    if (!hasNextPage && postsLength > 0) {
        return <p className="py-4 text-center text-text-secondary text-xs">No more posts</p>
    }
    return <div className="h-10" />
}
